// src/oura/components/KnowledgeReader.tsx
//
// One Knowledge source, opened from its row so staff can read what is in it
// (Jack, 20 Oct 2026: "the knowledge files are not clickable or readable").
// A PDF opens in the same PDF viewer the Reports and Baseline Snapshot drawers
// use; a pasted note, or any other file, shows the text Oura read from it, which
// is also exactly what every scan and draft is fed.

import { useEffect, useState } from "react";
import { DownloadIcon, ExternalLinkIcon, FileTextIcon } from "lucide-react";
import { getKnowledgeContent, type AeoKnowledgeSource, type KnowledgeContent } from "@service/aeo";
import { PDFViewer } from "@component/pdf-viewer";
import { Drawer, DrawerBlock, DrawerFold, DrawerNotice, DrawerPulse, PillLink, SURFACE, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { knowledgeDate } from "./KnowledgeEntry";
import { AnswerAsks, AnswerLines, AnswerRich, AnswerRow } from "./onboarding/AnswersDrawer";
import { cleanOption, noteSections } from "../onboarding/answerText";

/** The white block the note sits in; its first row loses the line above it. */
const NOTE_BLOCK = cn(SURFACE.block, "py-1 [&>*:first-child]:border-t-0 [&>ol>li:first-child]:border-t-0");

/**
 * A note laid out the way the Form answers drawer lays out the same answers:
 * customer questions as a numbered list, "Goals:" style parts as label and
 * answer rows. A note with neither reads as its own paragraphs.
 */
function NoteBody({ source, text }: { source: AeoKnowledgeSource; text: string }) {
    const sections = noteSections(text);
    if (source.label === "faq") {
        return (
            <div className={NOTE_BLOCK}>
                <AnswerAsks items={sections.flatMap((p) => (p.items.length ? p.items : p.text.split("\n")))} />
            </div>
        );
    }
    if (sections.some((p) => p.label)) {
        return (
            <div className={NOTE_BLOCK}>
                {sections.map((p, i) => {
                    const body = p.items.length
                        ? <AnswerLines items={p.items.map(cleanOption)} check={p.label === "Goals"} />
                        : <span className="whitespace-pre-line"><AnswerRich text={p.text} /></span>;
                    return p.label
                        ? <AnswerRow key={i} label={p.label}>{body}</AnswerRow>
                        : <div key={i} className={cn(TYPE.tb, "border-t border-gray-100 py-3 text-gray-900")}>{body}</div>;
                })}
            </div>
        );
    }
    return (
        <DrawerBlock>
            <p className="m-0 whitespace-pre-wrap break-words leading-relaxed">{text}</p>
        </DrawerBlock>
    );
}

function isPdf(s: AeoKnowledgeSource): boolean {
    return s.mime === "application/pdf" || /\.pdf$/i.test(s.title);
}

/** One fetch's outcome, and which business and source it was for. */
type ContentState = {
    key: string;
    value: KnowledgeContent | null | undefined;
    error: boolean;
};

export function KnowledgeReader({
    businessId,
    source,
    onClose,
}: {
    businessId: string;
    /** The source to show; null keeps the drawer closed. */
    source: AeoKnowledgeSource | null;
    onClose: () => void;
}) {
    // Keyed to the source it was fetched for, so opening another source never
    // shows the last one's text or download link while its own loads.
    const requestKey = source ? `${businessId}:${source.id}` : null;
    const sourceId = source?.id;
    const [contentState, setContentState] = useState<ContentState | null>(null);

    useEffect(() => {
        if (!sourceId || !requestKey) return;
        let live = true;
        setContentState({ key: requestKey, value: undefined, error: false });
        getKnowledgeContent(businessId, sourceId)
            .then((value) => {
                if (live) setContentState({ key: requestKey, value, error: false });
            })
            .catch(() => {
                if (live) setContentState({ key: requestKey, value: undefined, error: true });
            });
        return () => {
            live = false;
        };
    }, [businessId, sourceId, requestKey]);

    const current = contentState?.key === requestKey ? contentState : null;
    // undefined while loading, null when the server cannot give the contents yet.
    const content = current?.value;
    const contentError = current?.error === true;

    const pdf = !!source && isPdf(source) && !!content?.fileUrl;
    const text = content?.text?.trim() || null;

    const sub = source
        ? [
            source.kind === "text" ? "Pasted text" : "File",
            source.charCount ? `${source.charCount.toLocaleString()} characters` : null,
            knowledgeDate(source.createdAt) ? `added ${knowledgeDate(source.createdAt)}` : null,
            knowledgeDate(source.contentAsOf) ? `as of ${knowledgeDate(source.contentAsOf)}` : null,
        ].filter(Boolean).join(" · ")
        : undefined;

    const foot = content?.fileUrl && source ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
            {pdf && (
                <PillLink variant="secondary" icon={ExternalLinkIcon} href={content.fileUrl} target="_blank" rel="noopener noreferrer">
                    Open in new tab
                </PillLink>
            )}
            <PillLink variant="primary" icon={DownloadIcon} href={content.fileUrl} download={source.title}>
                Download
            </PillLink>
        </div>
    ) : undefined;

    return (
        <Drawer
            open={!!source}
            onClose={onClose}
            width={pdf ? 760 : 600}
            title={source?.title}
            sub={sub}
            foot={foot}
            itemKey={source?.id}
        >
            {source && (
                <div className="space-y-3">
                    {source.status === "failed" && source.errorDetail && <DrawerNotice>{source.errorDetail}</DrawerNotice>}
                    {source.status === "excluded" && (
                        <DrawerNotice>Parked. Scans and drafts do not read this until you press Use again.</DrawerNotice>
                    )}

                    {contentError ? (
                        <DrawerNotice>Couldn't load this. Close the reader and try again.</DrawerNotice>
                    ) : content === undefined ? (
                        <DrawerBlock busy>
                            <DrawerPulse lines={4} />
                        </DrawerBlock>
                    ) : content === null ? (
                        <div className="grid min-h-[40vh] place-items-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
                            <div className="max-w-sm">
                                <span aria-hidden className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-white text-gray-400 ring-1 ring-black/[0.06]">
                                    <FileTextIcon size={22} />
                                </span>
                                <p className={cn(TYPE.tt, "m-0 text-gray-900")}>Can't show the contents yet</p>
                                <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>Oura has it saved and scans read it. Showing it here is coming.</p>
                            </div>
                        </div>
                    ) : pdf ? (
                        <>
                            <PDFViewer url={content.fileUrl!} className="mx-auto" defaultZoom={1.1} />
                            {text && (
                                <DrawerFold id="staff-knowledge.text" label="Text Oura read from it" remember>
                                    <p className="m-0 whitespace-pre-wrap break-words">{text}</p>
                                </DrawerFold>
                            )}
                        </>
                    ) : source.kind === "text" && text ? (
                        <NoteBody source={source} text={text} />
                    ) : (
                        <DrawerBlock label={source.kind === "text" ? undefined : "Text Oura read from it"}>
                            {text ? (
                                <p className="m-0 whitespace-pre-wrap break-words leading-relaxed">{text}</p>
                            ) : (
                                <p className="m-0 text-gray-500">No text could be read from this.</p>
                            )}
                        </DrawerBlock>
                    )}
                </div>
            )}
        </Drawer>
    );
}
